import React, { useState } from "react";
import {
  Button,
  TextField,
  TextareaAutosize,
  Typography,
  Box,
  Container,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import {
  CheckCircle,
  Email,
  Language,
  LocationOn,
  Phone,
  YouTube,
} from "@mui/icons-material";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import BASE_URL from "../config/apiConfig";
import NewsletterSubscription from "../components/NewsLetter";
import FAQ from "../components/FAQ/FAQ"; // Adjust the path if needed

//#51843a blue colour

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "", // Changed from query to subject
    description: "",
  });
  const [dialogOpen, setDialogOpen] = useState(false);

  const validateEmail = (email) => {
    const re =
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?$/;
    return re.test(String(email).toLowerCase());
  };

  const validateForm = () => {
    return (
      formData.name && formData.email && formData.phone && formData.description
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      console.error("Form validation failed");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Successful:", data);
        setDialogOpen(true);
        // ✅ Clear the form after successful submission
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          description: "",
        });
      } else {
        console.error("Contact us failed");
      }
    } catch (error) {
      console.error("Error during Contact us:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="hero-banner"
        style={{
          width: "100%",
          height: "40vh",
          background: 'url("/Images/Contact_us.webp") center/cover no-repeat',
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          boxShadow: "inset 0 0 0 1000px rgba(0, 0, 0, 0.3)",
        }}
      >
        <h1
          className="hero-title"
          style={{
            fontSize: "3rem",
            fontWeight: "bold",
          }}
        >
          Contact Us
        </h1>
        <p
          className="breadcrumb"
          style={{
            fontSize: "1rem",
          }}
        >
          <a
            href="/"
            style={{
              color: "inherit",
              textDecoration: "none",
              fontWeight: "bold",
            }}
          >
            Home
          </a>{" "}
          /
          <a
            href="/contact"
            style={{
              color: "inherit",
              textDecoration: "none",
              fontWeight: "bold",
            }}
          >
            Contact Us
          </a>
        </p>
      </div>

      <Container maxWidth="lg" sx={{ paddingY: "48px" }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: 600, mb: 4, color: "#51843a" }} // Agritech color
        >
          Contact Us
        </Typography>
        <Typography align="center" paragraph sx={{ color: "#555", mb: 4 }}>
          We’d love to hear from you{" "}
        </Typography>

        <Box
          sx={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            boxShadow: 3,
            padding: { xs: 2, md: 4 },
            marginBottom: 6,
          }}
        >
          <Grid
            container
            spacing={4}
            alignItems="stretch"
            justifyContent="center"
          >
            {/* Left Side: Contact Form */}
            <Grid
              item
              xs={12}
              md={7}
              sx={{
                px: { xs: 1, sm: 2, md: 0 },
                mt: { xs: 2, md: 0 },

                // ✅ Center form on mobile, left on desktop
                mx: { xs: "auto", md: 0 },
                textAlign: { xs: "center", md: "left" },

                // ✅ Prevent shrinking on small devices
                width: { xs: "100%", sm: "90%", md: "100%" },
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  fontWeight: "bold",
                  color: "#333",
                  fontSize: { xs: "1.3rem", md: "1.7rem" }, // ✅ responsive title
                }}
              >
                Please enter your query here.
              </Typography>

              <form onSubmit={handleContact}>
                <Grid
                  container
                  spacing={{ xs: -2, md: 3 }}
                  justifyContent={{ xs: "center", md: "flex-start" }} // ✅ mobile center, desktop left
                  alignItems="center"
                >
                  {/* ✅ NAME FIELD */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      placeholder="First Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      variant="outlined"
                      fullWidth
                      required
                      InputProps={{
                        style: { fontSize: "0.9rem" },
                      }}
                      sx={{
                        bgcolor: "grey.100",
                        borderRadius: 2,
                        "& .MuiInputBase-input": {
                          padding: "12px",
                        },
                      }}
                    />
                  </Grid>

                  {/* ✅ EMAIL FIELD */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      placeholder="Email Address"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      variant="outlined"
                      fullWidth
                      required
                      InputProps={{
                        style: { fontSize: "0.9rem" },
                      }}
                      sx={{
                        bgcolor: "grey.100",
                        borderRadius: 2,
                        "& .MuiInputBase-input": {
                          padding: "12px",
                        },
                      }}
                      error={
                        !validateEmail(formData.email) &&
                        formData.email.length > 0
                      }
                      helperText={
                        !validateEmail(formData.email) &&
                        formData.email.length > 0
                          ? "Invalid email format"
                          : ""
                      }
                    />
                  </Grid>

                  {/* ✅ PHONE FIELD */}
                  <Grid item xs={12}>
                    <TextField
                      placeholder="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*$/.test(value) && value.length <= 10) {
                          handleInputChange(e);
                        }
                      }}
                      variant="outlined"
                      fullWidth
                      required
                      InputProps={{
                        style: { fontSize: "0.9rem" },
                      }}
                      sx={{
                        bgcolor: "grey.100",
                        borderRadius: 2,
                        "& .MuiInputBase-input": {
                          padding: "12px",
                        },
                      }}
                      error={
                        formData.phone.length > 0 &&
                        formData.phone.length !== 10
                      }
                      helperText={
                        formData.phone.length > 0 &&
                        formData.phone.length !== 10
                          ? "Phone number must be 10 digits"
                          : ""
                      }
                    />
                  </Grid>

                  {/* ✅ SUBJECT FIELD */}
                  <Grid item xs={12}>
                    <TextField
                      placeholder="Subject (Optional)"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        style: { fontSize: "0.9rem" },
                      }}
                      sx={{
                        bgcolor: "grey.100",
                        borderRadius: 2,
                        "& .MuiInputBase-input": {
                          padding: "12px",
                        },
                      }}
                    />
                  </Grid>

                  {/* ✅ DESCRIPTION / MESSAGE FIELD */}
                  <Grid item xs={12}>
                    <TextareaAutosize
                      placeholder="Your Message"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      minRows={4}
                      maxRows={6}
                      maxLength={4000}
                      style={{
                        width: "100%",
                        fontSize: "0.9rem",
                        borderRadius: "8px",
                        backgroundColor: "#f5f5f5",
                        border: "1px solid #ccc",
                        padding: "12px",
                        boxSizing: "border-box",
                      }}
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{
                        textAlign: "right",
                        color: "#999",
                        mt: 0.5,
                      }}
                    >
                      {formData.description.length}/4000 characters
                    </Typography>
                  </Grid>

                  {/* ✅ SUBMIT BUTTON */}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        bgcolor: "#51843a",
                        "&:hover": { bgcolor: "#2cabcb" },
                        borderRadius: 3,
                        py: { xs: 1.2, md: 1.5 },
                        fontSize: { xs: "0.95rem", md: "1rem" },
                      }}
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Submit"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {/* Right Side: Address Details */}
            {/* Right Side: Address Details */}
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                backgroundColor: "#f9f9f9",
                padding: { xs: 2, md: 3 },
                borderRadius: 8,
                boxShadow: 3,
                marginLeft: { xs: 4, md: 4 },
                mt: 4,
              }}
            >
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    mb: 2,
                    color: "#333",
                    textAlign: "center",
                    fontSize: { xs: "1.5rem", md: "1.8rem" },
                  }}
                >
                  Contact Us
                </Typography>

                <Box sx={{ mb: 2 }}>
                  {/* Address with Google Maps Link */}
                  <Typography
                    paragraph
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      padding: 1,
                      borderRadius: 4,
                      backgroundColor: "#b6f2c6",
                      color: "#51843a",
                      wordBreak: "break-word",
                    }}
                  >
                    <LocationOn sx={{ mr: 1 }} />
                    <a
                      href="https://maps.app.goo.gl/sTqQZCAiD82h3AhB8"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#51843a", textDecoration: "none" }}
                    >
                      Surobhi Agro Industries Private Limited- East India
                      coworking - Eden, 2nd floor, Hanuman Estates, 19, RN
                      Mukherjee Rd, Esplanade, Lal Bazar, Kolkata,
                      West Bengal 700001
                    </a>
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  {/* Phone Number */}
                  <Typography
                    paragraph
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: 1,
                      borderRadius: 4,
                      backgroundColor: "#b6f2c6",
                      color: "#51843a",
                      wordBreak: "break-word",
                    }}
                  >
                    <Phone sx={{ mr: 1 }} />
                    <a
                      href="tel:+917044245566"
                      style={{ color: "#51843a", textDecoration: "none" }}
                    >
                      +91 7044245566
                    </a>
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  {/* Email */}
                  <Typography
                    paragraph
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: 1,
                      borderRadius: 4,
                      backgroundColor: "#b6f2c6",
                      color: "#51843a",
                      wordBreak: "break-word",
                    }}
                  >
                    <Email sx={{ mr: 1 }} />
                    <a
                      href="mailto:info@farmologyindia.com"
                      style={{ color: "#51843a", textDecoration: "none" }}
                    >
                      info@farmologyindia.com
                    </a>
                  </Typography>
                </Box>

                <Box sx={{ mb: 0 }}>
                  {/* Website */}
                  <Typography
                    paragraph
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: 1,
                      borderRadius: 4,
                      backgroundColor: "#b6f2c6",
                      color: "#51843a",
                      wordBreak: "break-word",
                    }}
                  >
                    <Language sx={{ mr: 1 }} />
                    <a
                      href="https://www.farmology.ai"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#51843a", textDecoration: "none" }}
                    >
                      www.farmology.ai
                    </a>
                  </Typography>
                </Box>
              </Box>

              {/* Social Media Links */}
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                <a
                  href="https://www.facebook.com/p/Farmology-100063893500354/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Facebook size={24} style={{ color: "#3b5998" }} />
                </a>
                <a
                  href="https://www.youtube.com/channel/UCgjha5OQ4B0pdntZGAncvaQ"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <YouTube size={24} style={{ color: "#00acee" }} />
                </a>
                <a
                  href="https://www.instagram.com/farmo_logy/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Instagram size={24} style={{ color: "#e1306c" }} />
                </a>
                <a
                  href="https://in.linkedin.com/company/farmology-surobhi-agro"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin size={24} style={{ color: "#0e76a8" }} />
                </a>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Success Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Message Sent</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
            sx={{ p: 2 }}
          >
            <CheckCircle color="success" sx={{ fontSize: 50, mb: 2 }} />
            <Typography variant="h6">
              Your message has been sent successfully!
            </Typography>
            <Typography variant="body2">
              We'll get back to you as soon as possible.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* FAQ Section */}
      <Container maxWidth="lg" sx={{ paddingY: "40px" }}>
        <Typography variant="h4" gutterBottom>
          Frequently Asked Questions
        </Typography>
        <Box
          sx={{
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
            padding: { xs: 2, md: 3 },
            boxShadow: 2,
          }}
        >
          <FAQ />
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ paddingY: "40px" }}>
        {/* Add the Newsletter Subscription Component */}
        <NewsletterSubscription />
      </Container>
    </>
  );
}
